<template>
  <div class="tab-content">
    <div class="main-header">
      <div>
        <h1>{{ $t('controlCenter.dashboard.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.dashboard.subtitle') }}</p>
      </div>
      <button class="btn btn-primary" @click="fetchDashboard" :disabled="loading">
        <i class="fas fa-rotate-right"></i>
        {{ $t('controlCenter.common.refresh') }}
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="card-content">
          <div class="stat-icon icon-modules"><i class="fas fa-receipt"></i></div>
          <div class="stat-info">
            <div class="stat-label">{{ $t('controlCenter.dashboard.totalOrders') }}</div>
            <div class="stat-value">{{ dashboard.totalOrders }}</div>
          </div>
        </div>
      </div>
      <div class="stat-card">
        <div class="card-content">
          <div class="stat-icon icon-users"><i class="fas fa-circle-check"></i></div>
          <div class="stat-info">
            <div class="stat-label">{{ $t('controlCenter.dashboard.successfulOrders') }}</div>
            <div class="stat-value">{{ dashboard.successfulOrders }}</div>
          </div>
        </div>
      </div>
      <div class="stat-card">
        <div class="card-content">
          <div class="stat-icon icon-operations"><i class="fas fa-hourglass-half"></i></div>
          <div class="stat-info">
            <div class="stat-label">{{ $t('controlCenter.dashboard.pendingOrders') }}</div>
            <div class="stat-value">{{ dashboard.pendingOrders }}</div>
          </div>
        </div>
      </div>
      <div class="stat-card">
        <div class="card-content">
          <div class="stat-icon icon-pending"><i class="fas fa-circle-xmark"></i></div>
          <div class="stat-info">
            <div class="stat-label">{{ $t('controlCenter.dashboard.failedOrders') }}</div>
            <div class="stat-value">{{ dashboard.failedOrders }}</div>
          </div>
        </div>
      </div>
      <div class="stat-card revenue-card">
        <div class="card-content">
          <div class="stat-icon icon-users"><i class="fas fa-sack-dollar"></i></div>
          <div class="stat-info">
            <div class="stat-label">{{ $t('controlCenter.dashboard.totalRevenue') }}</div>
            <div class="stat-value">{{ formatAmount(dashboard.totalRevenue) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-grid-layout">
      <div class="main-column">
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">{{ $t('controlCenter.dashboard.trendTitle') }}</h2>
          </div>
          <div class="card-body">
            <el-table :data="dashboard.trend" v-loading="loading" style="width: 100%">
              <el-table-column :label="$t('controlCenter.dashboard.trendDate')" min-width="130">
                <template slot-scope="scope">
                  {{ scope.row.date }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('controlCenter.dashboard.trendOrders')" min-width="120">
                <template slot-scope="scope">
                  {{ scope.row.orderCount }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('controlCenter.dashboard.trendRevenue')" min-width="180">
                <template slot-scope="scope">
                  {{ formatAmount(scope.row.revenue) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2 class="card-title">{{ $t('controlCenter.dashboard.recentOrders') }}</h2>
          </div>
          <div class="card-body">
            <el-table :data="dashboard.recentOrders" v-loading="loading" style="width: 100%">
              <el-table-column prop="orderNo" :label="$t('controlCenter.dashboard.orderNo')" min-width="180"></el-table-column>
              <el-table-column prop="companyName" :label="$t('controlCenter.dashboard.company')" min-width="180"></el-table-column>
              <el-table-column :label="$t('controlCenter.dashboard.amount')" min-width="150">
                <template slot-scope="scope">
                  {{ formatAmount(scope.row.finalAmount, scope.row.displayCurrency) }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('controlCenter.common.status')" min-width="140">
                <template slot-scope="scope">
                  <el-tag size="small" :type="statusTagType(scope.row.status)">
                    {{ statusLabel(scope.row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="$t('controlCenter.dashboard.createdAt')" min-width="180">
                <template slot-scope="scope">
                  {{ formatDateTime(scope.row.createdAt) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>

      <div class="side-column">
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">{{ $t('controlCenter.dashboard.popularItems') }}</h2>
          </div>
          <div class="card-body">
            <div v-if="!dashboard.popularItems.length" class="empty-state">
              {{ $t('controlCenter.dashboard.noPopularItems') }}
            </div>
            <div v-else class="popular-list">
              <div v-for="item in dashboard.popularItems" :key="item.label" class="popular-item">
                <div class="popular-label">{{ item.label }}</div>
                <div class="popular-count">{{ item.count }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'
import { formatPrice } from '@/utils/currency'

export default {
  name: 'PaymentDashboard',
  data() {
    return {
      loading: false,
      dashboard: {
        totalOrders: 0,
        successfulOrders: 0,
        pendingOrders: 0,
        failedOrders: 0,
        totalRevenue: 0,
        trend: [],
        recentOrders: [],
        popularItems: []
      }
    }
  },
  created() {
    this.fetchDashboard()
  },
  methods: {
    async fetchDashboard() {
      this.loading = true
      try {
        const response = await paymentAdminApi.getDashboard()
        this.dashboard = response && response.data ? response.data : response
      } catch (error) {
        console.error('Failed to fetch dashboard:', error)
        this.$message.error(this.$t('controlCenter.dashboard.loadError'))
      } finally {
        this.loading = false
      }
    },
    formatAmount(amount, currency = 'RMB') {
      return `${currency} ${formatPrice(Number(amount || 0) / 100)}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return value.replace('T', ' ').slice(0, 19)
    },
    statusLabel(status) {
      const key = `controlCenter.statusLabels.${status || 'UNKNOWN'}`
      const text = this.$t(key)
      return text === key ? status || this.$t('controlCenter.common.unknown') : text
    },
    statusTagType(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'SUCCESS' || normalized === 'ACTIVE') return 'success'
      if (normalized === 'PENDING') return 'warning'
      return 'danger'
    }
  }
}
</script>

<style scoped>
.revenue-card .stat-value {
  font-size: 22px;
}

.popular-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.popular-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fbff;
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.popular-label {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}

.popular-count {
  min-width: 34px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(10, 127, 215, 0.1);
  color: #0a7fd7;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
}

.empty-state {
  padding: 20px 0;
  color: #64748b;
}

@media (max-width: 768px) {
  .revenue-card .stat-value {
    font-size: 20px;
    overflow-wrap: anywhere;
  }

  .popular-item {
    align-items: flex-start;
    gap: 10px;
  }

  .popular-label {
    min-width: 0;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  .popular-count {
    flex-shrink: 0;
  }
}
</style>
